import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { apiErrorText } from '@/components/pbo/apiErrorText'
import { failureError, invalidError } from '@/components/pbo/notice'
import { coreTableLayoutSlot } from '@/hooks/useCoreTableLayout'
import { usePermissionSlot } from '@/hooks/useMenuPermission'
import { findMissingRequired, requiredTitles, withRequired } from '@/utils/requiredCells'
import { BE_REQUIRED, GRID_CDS, HEAD, ITEM, MENU_CODE, MESSAGES, SAVE_REQUIRED, SUB, TABLE_IDS, type TableId } from './consts'
import {
  getDeleteImpact,
  screenSettingKeys,
  useDeleteScreenSettings,
  useGetButtonCodes,
  useGetColumnCodes,
  useGetHeads,
  useGetItems,
  useGetSubs,
  useSaveScreenSettings,
  type HeadParams,
  type ItemParams,
  type SaveRow,
  type SubParams,
} from './queries/screenSettings'
import ScreenManagementView, { type ActionCtx, type Row, type Wiring } from './ScreenManagementView'

/**
 * 화면관리(SY13) — 1.0 SY03 을 2.0 으로 옮긴 화면(LOGISPRD-16043, BE LOGISPRD-17091). 1.0 SY03 과 병행 운영하려고 새 메뉴코드 SY13 을 쓴다.
 *
 * 콤보관리(sy12)와 같은 배선 규약을 표 3개에 건다 — [추가]는 빈 행을 맨 위에, [저장]은 새 행 POST·기존 행 PUT, [삭제]는 DELETE 를 행별 순차로.
 * 위 표 행을 더블클릭하면 그 행의 키로 아래 표를 조회한다(HEAD → ITEM: 화주·메뉴, ITEM → SUB: +그리드). 중복은 BE 가 400 으로 거절한다.
 */
type EditRow = Row & { _id?: number }
type SavedRow = EditRow & { _id: number }
const isSaved = (r: EditRow): r is SavedRow => r._id != null
const toEdit = (rows: { _id: number }[]) => rows.map((r) => ({ ...r, key: String(r._id) }))

const apiErrMessage = (e: unknown) => apiErrorText(e, MESSAGES.apiFailed((e as { response?: { status?: number } })?.response?.status))
const doneOf = (e: unknown) => (e as { done?: number })?.done ?? 0
const text = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined)
const toHeadParams = (f: Record<string, unknown>): HeadParams => ({ shipperCode: text(f.SPR_NM), menuCode: text(f.MENU_CD), columnCode: text(f.COLUMN_CD) })
const NONE: never[] = []

/**
 * 표 하나의 편집본 — 재조회가 [추가]한 미저장 행과, 고쳤지만 저장하지 않은 기존 행(dirty)을 지우지 않는다.
 * 일부 행만 저장해도 나머지 행의 편집값이 남는다(리뷰 #1149). 저장된 행은 markSaved 로 dirty 에서 빼서 서버 값으로 바뀌게 한다.
 * [조회]·[초기화]·위 행 고르기는 reset 으로 편집을 버린다(1.0).
 */
function useEditRows(fetched: { _id: number }[] | undefined) {
  const [rows, setRows] = useState<EditRow[]>([])
  const dirty = useRef(new Set<string>())
  const source = fetched ?? NONE
  useEffect(() => {
    setRows((prev) => {
      const kept = new Map(prev.filter((r) => dirty.current.has(r.key)).map((r) => [r.key, r]))
      return [...prev.filter((r) => r._id == null), ...toEdit(source).map((r) => kept.get(r.key) ?? r)]
    })
  }, [source])
  /** 바뀐 칸이 있는 기존 행만 dirty 로 센다 — 표는 보이는 행 전부를 돌려준다 */
  const edit = (changed: Row[]) => {
    const byKey = new Map(changed.map((r) => [r.key, r]))
    setRows((prev) =>
      prev.map((r) => {
        const c = byKey.get(r.key)
        if (!c) return r
        if (r._id != null && Object.keys(c).some((k) => !Object.is(r[k], c[k]))) dirty.current.add(r.key)
        return { ...r, ...c }
      }),
    )
  }
  const reset = (next: EditRow[]) => {
    dirty.current.clear()
    setRows(next)
  }
  const markSaved = (keys: string[]) => keys.forEach((k) => dirty.current.delete(k))
  return { rows, setRows, edit, reset, markSaved }
}

function ScreenManagement() {
  const queryClient = useQueryClient()
  const [headParams, setHeadParams] = useState<HeadParams>({})
  const [itemParams, setItemParams] = useState<ItemParams & { key: string }>()
  const [subParams, setSubParams] = useState<SubParams & { key: string }>()
  const heads = useGetHeads(headParams)
  const items = useGetItems(itemParams && { shipperCode: itemParams.shipperCode, menuCode: itemParams.menuCode })
  const subs = useGetSubs(subParams && { shipperCode: subParams.shipperCode, menuCode: subParams.menuCode, gridCode: subParams.gridCode })
  const buttonCodes = useGetButtonCodes()
  const columnCodes = useGetColumnCodes(subParams?.gridCode)
  const save = useSaveScreenSettings()
  const remove = useDeleteScreenSettings()

  const permissionSlot = usePermissionSlot(MENU_CODE, GRID_CDS)
  const permission = useMemo(() => withRequired(permissionSlot, BE_REQUIRED), [permissionSlot])

  const head = useEditRows(heads.data)
  // 위 행을 안 골랐으면 아래 표는 비운다(조회가 꺼져 data 가 남아 있어도)
  const item = useEditRows(itemParams ? items.data : undefined)
  const sub = useEditRows(subParams ? subs.data : undefined)
  const state = { [HEAD]: head, [ITEM]: item, [SUB]: sub }
  const newSeq = useRef(0)

  const selectedRows = (table: TableId, ctx: ActionCtx) => {
    const picked = new Set(ctx.selected.map((r) => r.key))
    return state[table].rows.filter((r) => picked.has(r.key))
  }
  const clearPicks = (from: TableId) => {
    if (from === HEAD) {
      setItemParams(undefined)
      item.reset([])
    }
    setSubParams(undefined)
    sub.reset([])
  }

  const search = async (filters: Record<string, unknown>) => {
    const next = toHeadParams(filters)
    const same = JSON.stringify(next) === JSON.stringify(headParams)
    head.reset(same && heads.data ? toEdit(heads.data) : [])
    clearPicks(HEAD)
    setHeadParams(next)
    if (same) await queryClient.invalidateQueries({ queryKey: screenSettingKeys.list(HEAD, next) })
  }

  /** 새 행 — 아래 표는 고른 위 행의 키를 물려받는다. 화면관리 프리셋(행 추가 기본값)은 이 표에 있는 칸만 얹는다 */
  const blankRow = (table: TableId): EditRow => {
    newSeq.current += 1
    const base = { key: `new-${newSeq.current}`, __new: true, INS_DATE: '', INS_BY: '', UPD_DATE: '', UPD_BY: '' }
    const row: EditRow =
      table === HEAD
        ? { ...base, SPR_NM: '', MENU_CD: '', MENU_NM: '', BUTTONS: [] }
        : table === ITEM
          ? { ...base, SPR_NM: itemParams?.shipperCode, MENU_CD: itemParams?.menuCode, GRID_CD: '', TOOL_ADD: 'N', TOOL_RMV: 'N', TOOL_IMP: 'N', TOOL_EXP: 'N' }
          : { ...base, SPR_NM: subParams?.shipperCode, MENU_CD: subParams?.menuCode, GRID_CD: subParams?.gridCode, COLUMN_CD: '', COLUMN_REQ: 'N', COLUMN_CLTV: 'N', COLUMN_PRESET: '' }
    const presets = Object.entries(permission.forTable(table).presets).filter(([k]) => k in row)
    return { ...row, ...Object.fromEntries(presets) }
  }

  const wiring: Wiring = {
    permission,
    columnLayout: Object.fromEntries(TABLE_IDS.map((t) => [t, coreTableLayoutSlot(GRID_CDS[t])])),
    rows: {
      [HEAD]: () => ({ rows: head.rows, loading: heads.isFetching }),
      [ITEM]: () => ({ rows: item.rows, loading: items.isFetching }),
      [SUB]: () => ({ rows: sub.rows, loading: subs.isFetching }),
    },
    picked: {
      ...(itemParams && { [HEAD]: { key: itemParams.key, caption: `${itemParams.shipperCode} · ${itemParams.menuCode}` } }),
      ...(subParams && { [ITEM]: { key: subParams.key, caption: `${subParams.shipperCode} · ${subParams.menuCode} · ${subParams.gridCode}` } }),
    },
    options: {
      buttons: (buttonCodes.data ?? []).map((c) => ({ value: c, label: c })),
      columnCodes: (columnCodes.data ?? []).map((c) => ({ value: c, label: c })),
    },
    canAdd: (table) => table === HEAD || (table === ITEM ? itemParams != null : subParams != null),
    actions: {
      search,
      /** 같은 행을 다시 고르면 쿼리 키가 같아 data 참조가 안 바뀐다 — 그때만 캐시로 다시 채우고 무효화한다(search 와 같다, 1.0 은 다시 조회) */
      pick: (table, row) => {
        // 키는 서버에 저장된 값으로 — 고쳤지만 저장하지 않은 키로 아래 표를 조회하거나 새 행에 물려주지 않는다(리뷰 #1149)
        const saved: Record<string, unknown> = (table === HEAD ? heads.data : items.data)?.find((r) => r._id === row._id) ?? row
        const keys = { shipperCode: String(saved.SPR_NM), menuCode: String(saved.MENU_CD), key: row.key }
        if (table === HEAD) {
          const same = itemParams?.shipperCode === keys.shipperCode && itemParams.menuCode === keys.menuCode
          clearPicks(HEAD)
          setItemParams(keys)
          if (same && items.data) {
            item.reset(toEdit(items.data))
            queryClient.invalidateQueries({ queryKey: screenSettingKeys.list(ITEM, { shipperCode: keys.shipperCode, menuCode: keys.menuCode }) }).catch(() => undefined)
          }
        } else {
          const next = { ...keys, gridCode: String(saved.GRID_CD) }
          const same = subParams?.shipperCode === next.shipperCode && subParams.menuCode === next.menuCode && subParams.gridCode === next.gridCode
          sub.reset(same && subs.data ? toEdit(subs.data) : [])
          setSubParams(next)
          if (same) queryClient.invalidateQueries({ queryKey: screenSettingKeys.list(SUB, { shipperCode: next.shipperCode, menuCode: next.menuCode, gridCode: next.gridCode }) }).catch(() => undefined)
        }
      },
      add: (table) => state[table].setRows((prev) => [blankRow(table), ...prev]),
      /** 편집 셀 — 표는 보이는 행만 돌려준다. 행 키로 찾아 바뀐 것만 갈아 끼운다 */
      rowsChange: (table, changed) => state[table].edit(changed),
      /** [저장] 확인 — 검증·API 실패 모두 throw(확인창·선택 유지). 실패 지점까지 POST 된 새 행은 미저장 목록에서 뺀다(다시 저장하면 중복으로 거절된다) */
      save: async (table, ctx) => {
        const targets = selectedRows(table, ctx)
        if (targets.length === 0) throw invalidError(MESSAGES.noSelection)
        const required = permission.forTable(table).columns(SAVE_REQUIRED[table])
        if (findMissingRequired(targets, required)) throw invalidError(MESSAGES.requiredCells(requiredTitles(required)))
        const dropPosted = (done: number) => {
          state[table].markSaved(targets.slice(0, done).map((r) => r.key))
          const posted = new Set(targets.slice(0, done).filter((r) => r._id == null).map((r) => r.key))
          state[table].setRows((prev) => prev.filter((r) => !posted.has(r.key)))
        }
        try {
          await save.mutateAsync(targets.map((row): SaveRow => ({ table, row })))
          dropPosted(targets.length)
          await queryClient.invalidateQueries({ queryKey: screenSettingKeys.all })
        } catch (e) {
          const done = doneOf(e)
          dropPosted(done)
          throw failureError(done > 0 ? MESSAGES.partialFailed(targets.length, done, apiErrMessage(e)) : apiErrMessage(e))
        }
      },
      /** [삭제] 확인 — 미저장 새 행은 화면에서만 지우고, 저장된 행은 행별 DELETE(BE 가 아래 표 행을 함께 지운다) */
      remove: async (table, ctx) => {
        const targets = selectedRows(table, ctx)
        const local = new Set(targets.filter((r) => r._id == null).map((r) => r.key))
        state[table].setRows((prev) => prev.filter((r) => !local.has(r.key)))
        const saved = targets.filter(isSaved)
        if (saved.length === 0) return
        // 고른 위 행이 지워지면 아래 표 조회 기준이 사라진다
        const pickedKey = table === HEAD ? itemParams?.key : table === ITEM ? subParams?.key : undefined
        try {
          await remove.mutateAsync({ table, ids: saved.map((r) => r._id) })
          if (pickedKey && saved.some((r) => r.key === pickedKey)) clearPicks(table)
          await queryClient.invalidateQueries({ queryKey: screenSettingKeys.all })
        } catch (e) {
          const done = doneOf(e)
          const deleted = new Set(saved.slice(0, done).map((r) => r.key))
          state[table].setRows((prev) => prev.filter((r) => !deleted.has(r.key)))
          if (pickedKey && deleted.has(pickedKey)) clearPicks(table)
          const removed = local.size + done
          throw failureError(removed > 0 ? MESSAGES.partialFailed(targets.length, removed, apiErrMessage(e)) : apiErrMessage(e))
        }
      },
    },
    /** HEAD 는 함께 지워지는 ITEM·SUB 건수를 BE 에 묻는다(1.0 deleteForm.py 연쇄 삭제). 실패하면 알리고 확인창을 열지 않는다 */
    deleteNotice: async (table, ctx) => {
      if (table === SUB) return undefined
      const saved = selectedRows(table, ctx).filter(isSaved)
      if (saved.length === 0) return undefined
      if (table === ITEM) return MESSAGES.itemCascade
      try {
        const impacts = await Promise.all(saved.map((r) => getDeleteImpact(r._id)))
        return MESSAGES.cascade(
          impacts.reduce((n, i) => n + i.gridCount, 0),
          impacts.reduce((n, i) => n + i.columnCount, 0),
        )
      } catch (e) {
        throw failureError(apiErrMessage(e))
      }
    },
    useBusy: () => save.isPending || remove.isPending,
    onReset: () => {
      search({}).catch(() => undefined)
    },
  }

  return <ScreenManagementView wiring={wiring} />
}

export default ScreenManagement
